/**
 * Per-measure math for Gap Management rows and the scenario composer.
 *
 * Extracted verbatim (2026-10-19, P1) from the `rows` memo in
 * src/pages/programs/mips/gap-management/quality/Summary.tsx. Behavior is pinned by
 * src/lib/scenario/__tests__/measure-math.parity.test.ts against a frozen copy of the
 * original inline code — change both only on purpose.
 */
import {
  scoreQualityMeasure,
  computeDataCompletenessRate,
  computePerformanceRate,
  calculateOverallPerformanceRate,
  calculateOverallDataCompletenessRate,
  type PracticeSize,
  type ScoringResult,
} from '@/lib/mips-scoring';
import type { StrataConfig, VbcaBenchmark } from '@/lib/mips-data-service';
import { countsForAlgorithm } from '@/lib/strata-counts';
import { hasCustomStrata, getCustomStrataAlgorithm } from '@/lib/custom-strata-utils';

export type SimRule = 'none' | 'no75' | 'noden' | 'both';

export interface MeasureCounts {
  ip: number;
  ex: number;
  den: number;
  met: number;
  exc: number;
  not: number;
}

/** The subset of MipsDataService the math needs (the singleton satisfies it). */
export interface MeasureDataSource {
  getStrataConfig(measureId: string, submissionMethod?: string): StrataConfig | null;
  getStrataDefinitions(measureId: string): Array<{ name?: string; description: string }>;
  getBenchmarksForMeasure(measureId: string): VbcaBenchmark[];
}

/** One Gap Management baseData row (shape produced by Summary's data load). */
export interface MeasureSource {
  id: string;
  submissionMethod?: string;
  title?: string;
  ip?: number;
  ex?: number;
  den?: number;
  met?: number;
  exc?: number;
  not?: number;
  isMultiStrata?: boolean;
  strata?: any[];
  overallAlgorithm?: string;
  calculationMode?: string;
  manualValues?: any;
}

/** Unsaved edits for one row (Summary's `drafts[compositeId]`). */
export interface MeasureDraft {
  manual?: Partial<MeasureCounts>;
  strata?: any[];
}

export interface DerivedMeasure {
  counts: MeasureCounts;
  prBase: number;
  dc: number;
  algorithm: string | undefined;
  isManualMode: boolean;
  /** Split-algorithm strata measure (PR and DC from different strata, e.g. 226/431/510). */
  isSplitStrata: boolean;
  /** Strata to display: draft strata > saved manual strata > calculated strata. */
  displayStrata: any[] | undefined;
}

export interface MeasureScenarioOptions {
  practiceSize: PracticeSize;
  simRule: SimRule;
  /** Performance-rate improvement in percentage points (0 = none). */
  simDelta: number;
  benchmark: VbcaBenchmark | null | undefined;
}

export interface MeasureScenarioResult {
  /** Points shown: what-if points when a simRule is active, otherwise baseline. */
  points: number;
  pointsImproved: number;
  potentialGain: number;
  prImproved: number;
  baseline: ScoringResult;
  whatIf: ScoringResult;
  improved: ScoringResult;
}

/** The benchmark Gap Management rows score against: exact collection-type match only. */
export function findExactBenchmark(
  ds: MeasureDataSource,
  measureId: string,
  submissionMethod: string | undefined,
): VbcaBenchmark | undefined {
  return ds.getBenchmarksForMeasure(measureId).find(b => b.submissionMethods === submissionMethod);
}

/** Counts, performance rate and data completeness for one row (manual mode, drafts and strata aware). */
export function deriveMeasure(
  src: MeasureSource,
  measureId: string,
  submissionMethod: string | undefined,
  draft: MeasureDraft | undefined,
  ds: MeasureDataSource,
): DerivedMeasure {
  const isManualMode = src.calculationMode === 'manual';
  const manualValues = src.manualValues;

  let ip, ex, den, met, exc, not;

  if (isManualMode && manualValues) {
    ip = manualValues.ip || 0;
    ex = manualValues.ex || 0;
    den = manualValues.den || 0;
    met = manualValues.met || 0;
    exc = manualValues.exc || 0;
    not = manualValues.not || 0;

    const draftValues = draft?.manual;
    if (draftValues) {
      ip = draftValues.ip ?? ip;
      ex = draftValues.ex ?? ex;
      den = Math.max(0, ip - ex); // Always recalculate den
      met = draftValues.met ?? met;
      exc = draftValues.exc ?? exc;
      not = draftValues.not ?? not;
    }
  } else {
    ip = src.ip ?? 0;
    ex = src.ex ?? 0;
    den = src.den ?? 0;
    met = src.met ?? 0;
    exc = src.exc ?? 0;
    not = src.not ?? 0;
  }

  let prBase: number, dc: number;
  let algorithm: string | undefined = src.overallAlgorithm;

  if (src.isMultiStrata && src.strata) {
    const strataConfig = ds.getStrataConfig(measureId, submissionMethod);
    const strataDefinitions = ds.getStrataDefinitions(measureId);

    const draftStrata = draft?.strata;
    const hasDraftStrata = draftStrata && Array.isArray(draftStrata) && draftStrata.length > 0;
    const savedManualStrata = isManualMode && manualValues?.strata;
    const hasSavedManualStrata = savedManualStrata && Array.isArray(savedManualStrata) && savedManualStrata.length > 0;

    // Priority: draft strata > saved manual strata > calculated source strata
    const strataToUse = hasDraftStrata ? draftStrata : (hasSavedManualStrata ? savedManualStrata : src.strata);

    const sanitizedStrata = strataToUse.map((s: any, idx: number) => {
      const original = src.strata![idx] || {};
      return {
        ...original,
        ...s,
        ip: Number(s.ip ?? original.ip ?? 0),
        ex: Number(s.ex ?? original.ex ?? 0),
        den: Number(s.den ?? original.den ?? 0),
        met: Number(s.met ?? original.met ?? 0),
        exc: Number(s.exc ?? original.exc ?? 0),
        not: Number(s.not ?? original.not ?? 0),
      };
    });

    if (hasCustomStrata(strataToUse)) {
      // Custom strata always use weighted average
      const customAlgorithm = getCustomStrataAlgorithm();
      prBase = calculateOverallPerformanceRate(sanitizedStrata, customAlgorithm, strataDefinitions);
      dc = calculateOverallDataCompletenessRate(sanitizedStrata, customAlgorithm, strataDefinitions);
      const algoCounts = countsForAlgorithm(sanitizedStrata, customAlgorithm, strataDefinitions);
      ip = algoCounts.ip;
      ex = algoCounts.ex;
      den = algoCounts.den;
      met = algoCounts.met;
      not = algoCounts.not;
      exc = algoCounts.exc;
      algorithm = customAlgorithm;
    } else if (strataConfig) {
      prBase = calculateOverallPerformanceRate(sanitizedStrata, strataConfig.performanceRate, strataDefinitions);
      dc = calculateOverallDataCompletenessRate(sanitizedStrata, strataConfig.completeRate, strataDefinitions);
      // Split-algorithm measures: display & case-minimum use the DC stratum's counts.
      const isSplitAlgorithm = strataConfig.completeRate !== strataConfig.performanceRate;
      const displayCounts = countsForAlgorithm(
        sanitizedStrata,
        isSplitAlgorithm ? strataConfig.completeRate : strataConfig.performanceRate,
        strataDefinitions,
      );
      ip = displayCounts.ip;
      ex = displayCounts.ex;
      den = displayCounts.den;
      met = displayCounts.met;
      not = displayCounts.not;
      exc = displayCounts.exc;
      algorithm = strataConfig.performanceRate;
    } else {
      prBase = computePerformanceRate(met, not, exc);
      dc = computeDataCompletenessRate(den, met, not, exc);
    }
  } else {
    prBase = computePerformanceRate(met, not, exc);
    dc = computeDataCompletenessRate(den, met, not, exc);
  }

  const isSplitStrata = Boolean(src.isMultiStrata && src.strata &&
    (() => {
      const cfg = ds.getStrataConfig(measureId, submissionMethod);
      return cfg ? cfg.completeRate !== cfg.performanceRate : false;
    })());

  return {
    counts: { ip, ex, den, met, exc, not },
    prBase,
    dc,
    algorithm,
    isManualMode,
    isSplitStrata,
    displayStrata: draft?.strata || (isManualMode && manualValues?.strata ? manualValues.strata : src.strata),
  };
}

/** Baseline, what-if (simRule) and improved (simDelta) points for one derived row. */
export function scoreMeasure(derived: DerivedMeasure, opts: MeasureScenarioOptions): MeasureScenarioResult {
  const { counts: { den, met, exc, not }, prBase, dc, isSplitStrata } = derived;
  const { practiceSize, simRule, simDelta, benchmark } = opts;

  // Split strata: counts come from the DC stratum while PR comes from another, so
  // pass precomputed rates instead of letting scoreQualityMeasure re-derive them.
  const scoringOverrides = isSplitStrata
    ? { overridePerformanceRate: prBase, overrideDataCompletenessRate: dc }
    : {};

  const isInverse = benchmark?.isInverse === 'TRUE';
  const prImproved = isInverse
    ? Math.max(0, prBase - simDelta)   // inverse measures improve downward
    : Math.min(100, prBase + simDelta);

  const baseline = scoreQualityMeasure({
    denominator: den,
    performanceMet: met,
    performanceNotMet: not,
    numeratorExceptions: exc,
    practiceSize,
    benchmark: benchmark || null,
    ...scoringOverrides,
  });

  const whatIf = scoreQualityMeasure({
    denominator: den,
    performanceMet: met,
    performanceNotMet: not,
    numeratorExceptions: exc,
    practiceSize,
    benchmark: benchmark || null,
    ignoreCompleteness: simRule === 'no75' || simRule === 'both',
    ignoreCaseMinimum: simRule === 'noden' || simRule === 'both',
    ...scoringOverrides,
  });

  const points = (simRule !== 'none') ? whatIf.score : baseline.score;

  // Improved rate: keep complete cases constant, only redistribute met / not met.
  const totalPerformanceCases = met + not;
  const improvedMet = Math.round((prImproved / 100) * totalPerformanceCases);
  const improved = scoreQualityMeasure({
    denominator: den,
    performanceMet: improvedMet,
    performanceNotMet: totalPerformanceCases - improvedMet,
    numeratorExceptions: exc,
    practiceSize,
    benchmark: benchmark || null,
    // Rate simulations always respect case minimum and data completeness.
    ignoreCompleteness: false,
    ignoreCaseMinimum: false,
    // Split strata: DC does not change with a PR simulation; PR is re-derived from the improved counts.
    ...(isSplitStrata ? { overrideDataCompletenessRate: dc } : {}),
  });
  const pointsImproved = improved.score;

  return {
    points,
    pointsImproved,
    potentialGain: (simRule !== 'none')
      ? whatIf.score - baseline.score
      : pointsImproved - points,
    prImproved,
    baseline,
    whatIf,
    improved,
  };
}
